import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useSearch } from "@tanstack/react-router";
import { Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Input from "components/ui/input/Input";
import Label from "components/ui/input/Label";
import { betterAuthErrorText } from "lib/auth/authClient";
import { useAwsWafWarmup } from "lib/auth/useAwsWafWarmup";
import { useRequestPasswordReset } from "lib/hooks/api/auth/useRequestPasswordReset";
import { Card, Field, Form, Page } from "./authPageStyles";
import { StotlesLogo } from "./StotlesLogo";

type FormValues = {
  email: string;
};

// Unauthenticated entry point for the password-reset flow, reached from the login page's "Forgot
// your password?" link. Collects an email and kicks off the same better-auth reset used by the
// logged-in account settings page; the emailed link lands on /auth/reset-password.
export function BetterAuthForgotPasswordPage() {
  // Pre-filled from the login page's email field (validateForgotPasswordSearch). `strict: false`
  // lets it resolve to undefined when rendered outside its route (e.g. tests).
  const { email } = useSearch({ strict: false });
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { email: email ?? "" },
  });

  // Set on the first (successful) send — its presence is what switches the page from the form to
  // the confirmation view, and it holds the address so a resend has somewhere to send to. Latched
  // rather than read from the mutation's `isSuccess`: resending re-runs the mutation, which drops
  // `isSuccess` back to false while it's in flight and would bounce the user back to the form
  // mid-resend.
  const [sentTo, setSentTo] = useState<string | null>(null);
  // Set on the second send (the resend), and disables sending a third time — only one resend is
  // allowed. Only flips once that resend actually succeeds — set eagerly on click, a failed resend
  // would permanently disable the button under a "Reset link sent" label that isn't true, with no
  // way to retry short of a reload. Mirrors CheckYourEmailCard's isSuccess-gated resend state.
  const [hasResent, setHasResent] = useState(false);

  const { mutate: requestReset, isError, error, isLoading } = useRequestPasswordReset();
  const warmWaf = useAwsWafWarmup();

  // Deliberately generic: better-auth doesn't reveal whether an address has an account, and neither
  // should this screen. That's why it can't share CheckYourEmailCard — that card names the address
  // as a fact ("We've sent an email to …"), which would confirm the account exists. Same structure,
  // hedged copy.
  if (sentTo) {
    return (
      <Page>
        <Card centred>
          <StotlesLogo />
          <Text h1>Check your email</Text>
          <Text color="sysTextSecondary">
            If an account exists for <strong>{sentTo}</strong>, we&apos;ve sent a link to reset your
            password.
          </Text>
          <Text color="sysTextSecondary">
            Didn&apos;t get it? Check your spam folder, then resend the link.
          </Text>
          <Text color="sysTextSecondary">This reset link will expire in 1 hour.</Text>
          <Button
            buttonType="secondary"
            fullWidth
            loading={isLoading}
            disabled={hasResent}
            onClick={() => requestReset({ email: sentTo }, { onSuccess: () => setHasResent(true) })}
          >
            {hasResent ? "Reset link sent" : "Resend reset link"}
          </Button>
          {isError && (
            <Text color="sysDestructiveDefault">
              {betterAuthErrorText(error, "Could not resend password reset email")}
            </Text>
          )}
          <Text color="sysTextSecondary">
            <Link to="/auth/login" search={{ email: sentTo || undefined }}>
              Back to log in
            </Link>
          </Text>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <Card>
        <StotlesLogo />
        <Text h1>Reset your password</Text>
        <Text color="sysTextSecondary">
          Enter your email address and we&apos;ll send you a link to reset your password.
        </Text>
        <Form
          onSubmit={handleSubmit(({ email }) =>
            // Per-call callback rather than a hook option: useRequestPasswordReset takes none, and
            // it's also consumed by the settings page, so it stays as it is.
            requestReset({ email }, { onSuccess: () => setSentTo(email) }),
          )}
          onFocusCapture={warmWaf}
        >
          <Field>
            <Label htmlFor="email">Email</Label>
            <Input
              name="email"
              control={control}
              type="email"
              placeholder="name@email.com"
              rules={{
                required: { value: true, message: "Email is required" },
              }}
            />
            {errors.email?.message && (
              <Text color="sysDestructiveDefault">{errors.email.message}</Text>
            )}
          </Field>

          {isError && (
            <Text color="sysDestructiveDefault">
              {betterAuthErrorText(error, "Could not send password reset email")}
            </Text>
          )}

          <Button buttonType="primary" type="submit" fullWidth loading={isLoading}>
            Send reset link
          </Button>
        </Form>

        <Text color="sysTextSecondary">
          Remembered it? <Link to="/auth/login">Log in</Link>
        </Text>
      </Card>
    </Page>
  );
}
